/**
 * Signup confirmation. `POST /signup/v1` creates the tenant, the owner account,
 * the seeded catalogs and the trial subscription, but deliberately returns no
 * tokens — the owner signs in through the normal login flow, which is where the
 * mandatory Team Admin MFA enrolment happens.
 */
import { Link } from 'react-router-dom';

import type { SignupResult } from '../../lib/api-client';
import { ROUTES, TRIAL } from '../../lib/constants';

function formatDate(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime())
    ? iso
    : parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export function SignupSuccess({ result }: { result: SignupResult }): JSX.Element {
  return (
    <div className="stack" data-testid="signup-success">
      <div className="banner banner--success" role="status">
        <span className="banner__title">{result.name} is ready</span>
        <span>
          Your team portal, stat catalog and {TRIAL.days}-day {TRIAL.planDisplayName} trial are set
          up.
        </span>
      </div>

      <dl className="summary-list">
        <dt>Team code</dt>
        <dd data-testid="signup-result-slug">{result.slug}</dd>
        <dt>Portal address</dt>
        <dd>/app/{result.slug}</dd>
        <dt>Tenant ID</dt>
        <dd className="mono" data-testid="signup-result-tenant-id">
          {result.tenant_id}
        </dd>
        <dt>Team Admin</dt>
        <dd>{result.owner_email}</dd>
        <dt>Trial ends</dt>
        <dd>{formatDate(result.trial_expires_at)}</dd>
        <dt>Seeded</dt>
        <dd>
          {result.onboarding.cohort_values} cohorts · {result.onboarding.enum_values} catalog values
          · {result.onboarding.stat_definitions} stat definitions
        </dd>
      </dl>

      <p className="muted">
        Give your staff the team code <strong>{result.slug}</strong> — they need it to sign in. Your
        first sign-in will ask you to enrol an authenticator app, then the setup wizard at{' '}
        <span className="mono">/app/{result.slug}/welcome</span> walks staff invites and your first
        plays.
      </p>

      <Link
        className="btn btn--primary btn--block"
        to={`${ROUTES.login}?team=${encodeURIComponent(result.slug)}`}
      >
        Continue to sign in
      </Link>
    </div>
  );
}
